import type { TaskListFeatureSuggestionState } from '../../../../../common/tiptap/extensions/taskList/taskListFeatureSuggestionUtils';

export const SUGGEST = 'suggest';
export const UNSUGGEST = 'unsuggest';

/** `null` means leave the current suggestion state alone. */
export type SuggestionChange = typeof SUGGEST | typeof UNSUGGEST | null;

/**
 * Decides whether adding a title should be suggested after a document change.
 *
 * Only a task being added triggers the suggestion, so an untitled list that already has
 * plenty of tasks is never nagged when it loads.
 */
export const getTitleSuggestionChange = ({
    documentState,
    previousTopLevelTaskCount,
    isAcknowledged,
    isSuggested,
}: {
    documentState: TaskListFeatureSuggestionState;
    previousTopLevelTaskCount: number;
    isAcknowledged: boolean;
    isSuggested: boolean;
}): SuggestionChange => {
    if (isAcknowledged) return null;

    if (documentState.hasTitle) return isSuggested ? UNSUGGEST : null;

    if (isSuggested) return null;

    const gainedTask = documentState.topLevelTaskCount > previousTopLevelTaskCount;

    return gainedTask && documentState.topLevelTaskCount > 1 ? SUGGEST : null;
};

/**
 * Decides whether deleting completed tasks should be suggested after a document change.
 *
 * Two completions are needed to start suggesting, but the suggestion stays until every
 * completion is cleared, so unchecking one task doesn't make it flicker away.
 */
export const getDeleteTasksSuggestionChange = ({
    documentState,
    isDismissed,
    isSuggested,
}: {
    documentState: TaskListFeatureSuggestionState;
    isDismissed: boolean;
    isSuggested: boolean;
}): SuggestionChange => {
    if (isDismissed) return null;

    const { completedTaskCount } = documentState;

    if (!isSuggested && completedTaskCount >= 2) return SUGGEST;

    if (isSuggested && completedTaskCount === 0) return UNSUGGEST;

    return null;
};
